let loggingOut = false;
let revokeTokensOnSignout = false;

const LOG_PREFIX = '[auth/logout-race]';

/** Competing automatic sign-in sites from #8231 investigation. */
export type LogoutRaceSource =
  | 'AuthPage:appstore-user-missing'
  | 'AuthPage:not-authenticated'
  | 'ProviderAppAuth:access-token-expired'
  | 'ProviderAppAuth:server-token-expired'
  | 'useHandleSignOut';

const log = (event: string, detail?: Record<string, unknown>): void => {
  console.warn(LOG_PREFIX, event, detail ?? '');
};

/** Set before OIDC signoutRedirect so automatic re-auth does not race logout. */
export const setLoggingOut = (): void => {
  loggingOut = true;
  log('logout started — automatic signinRedirect will be suppressed until full-page navigation', {
    note: 'auth guard can fire before the browser navigates to end_session_endpoint; suppress re-auth until then',
  });
};

/**
 * Lift the suppression when logout did not navigate away after all. Without this the flag
 * outlives the failed attempt and every automatic sign-in stays blocked for the lifetime of
 * the document, stranding the user on the loader until they reload by hand.
 */
export const clearLoggingOut = (reason: string): void => {
  if (!loggingOut) {
    return;
  }
  loggingOut = false;
  log('logout did not navigate — re-enabling automatic signinRedirect', { reason });
};

export const isLoggingOut = (): boolean => loggingOut;

/**
 * Record the deployment's `oidc.revoke_tokens_on_signout` setting as soon as the environment
 * reports it. Deliberately module state rather than context: `ErrorBoundary` renders its error
 * pages *instead of* its children, so `Error403Page` — which offers a Sign out button — sits
 * outside `EnvProvider`, the query client, and the router. A context or query read there resolves
 * to the default and silently ignores the deployment's setting.
 */
export const setRevokeTokensOnSignout = (enabled: boolean): void => {
  revokeTokensOnSignout = enabled;
};

/**
 * Whether to revoke tokens before `signoutRedirect`. False until the environment has been read,
 * which matches the documented default — pre-revocation can terminate the session-bound Keycloak
 * session and skip federated IdP logout, so failing closed here is the safe direction.
 */
export const shouldRevokeTokensOnSignout = (): boolean => revokeTokensOnSignout;

/** Log and return true when sign-in must not run (would cancel in-flight logout). */
export const shouldSuppressAutoSignIn = (source: LogoutRaceSource): boolean => {
  if (!loggingOut) {
    return false;
  }
  log('blocked signinRedirect — would race signoutRedirect', { source });
  return true;
};

export const logAutoSignInRedirect = (source: LogoutRaceSource): void => {
  log('signinRedirect', { source });
};

export const logSignoutRedirect = (): void => {
  log('signoutRedirect invoked', {
    source: 'useHandleSignOut',
  });
};

export const logLogoutRaceEvent = (event: string, detail?: Record<string, unknown>): void => {
  log(event, detail);
};
